import React, { useState, useRef, useEffect } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
} from '@mui/material';
import Barcode from 'react-barcode';
import { useReactToPrint } from 'react-to-print';
import { useParams } from 'react-router-dom';
import { useGetProductQuery } from 'state/api';
import { PageContainer, formatCurrency } from 'components/shared';

const GenerateBarcode = () => {
  const { productid } = useParams();
  const { data: productResp } = useGetProductQuery(productid, { skip: !productid });
  const product = productResp?.data;

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [maxQuantity] = useState(270);

  // Prefill from the real product when arriving via /products/barcode/:productid
  useEffect(() => {
    if (product) {
      setCode(product.barcode || product.productCode || '');
      setName(product.name || '');
      setPrice(product.sellingPrice ?? 0);
    }
  }, [product]);

  // Reference for Printing
  const printRef = useRef(null);

  const handleCodeChange = (event) => setCode(event.target.value);
  const handleNameChange = (event) => setName(event.target.value);
  const handleQuantityChange = (event) =>
    setQuantity(Math.min(parseInt(event.target.value, 10) || 1, maxQuantity));

  const generateBarcodes = () => {
    const newCodes = Array.from({ length: quantity }, (_, i) => `${code}${i.toString().padStart(3, '0')}`);
    setGeneratedCodes(newCodes);
  };

  const handleReset = () => {
    setCode(product?.barcode || product?.productCode || '');
    setName(product?.name || '');
    setPrice(product?.sellingPrice ?? 0);
    setQuantity(1);
    setGeneratedCodes([]);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef, // v3 API: ref object to the printable content
    documentTitle: 'Generated_Barcodes',
    // v3 cleans up the print iframe by default (preserveAfterPrint defaults false)
  });

  return (
    <PageContainer
      title="Barcode"
      subtitle="Generate and print product barcode labels"
    >
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Generate Barcodes
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField label="Code" variant="outlined" fullWidth value={code} onChange={handleCodeChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Name" variant="outlined" fullWidth value={name} onChange={handleNameChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Quantity"
                variant="outlined"
                fullWidth
                type="number"
                value={quantity}
                onChange={handleQuantityChange}
                helperText={`Max: ${maxQuantity}`}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary" onClick={generateBarcodes}>
                  Generate
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  Reset
                </Button>
                <Button variant="outlined" onClick={handlePrint} disabled={generatedCodes.length === 0}>
                  Print
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {generatedCodes.length > 0 && (
        <Card>
          <CardContent>
            <div ref={printRef}>
              <Grid container spacing={2}>
                {generatedCodes.map((c, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={index}
                    sx={{ textAlign: 'center' }}
                  >
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(price)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Code: {c}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Barcode value={c} format="CODE128" width={2} height={50} displayValue={false} />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
};

export default GenerateBarcode;
